import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/report-form/report-form.scss';
import { FormWidget } from '../widgets/form-widget';
import { AttachmentsListWidget } from '../widgets/attachments-list-widget';
import { ReportFormWidget } from '../widgets/report-form-widget';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { initReportFormSignWidget } from '../widgets/sign-widget/report-form-sign-widget';
import type { ReportFormSignPresenter } from '../widgets/sign-widget/report-form-sign-presenter';

class ReportForm {
  private formWidget: FormWidget;
  private reportFormWidget: ReportFormWidget;
  private attachmentsListWidget: AttachmentsListWidget;

  private formElement: HTMLFormElement | null = null;
  private formSubmitButton: HTMLButtonElement | null = null;
  private reportFormSignPresenter: ReportFormSignPresenter | null = null;
  private readonly EDIT_MODAL_ID = '#editReportConfirmModal';

  constructor() {
    this.formElement = document.querySelector('[id^="reportForm"]') as HTMLFormElement | null;

    this.formWidget = new FormWidget({
      formSelector: '[id^="reportForm"]',
      submitUrl: getApiUrl('/api/reportForm.php'),
    });

    this.attachmentsListWidget = new AttachmentsListWidget({
      inputSelector: '[id^="reportAttachmentsInput"]',
      listSelector: '[id^="reportAttachmentsList"]',
    });

    this.reportFormWidget = new ReportFormWidget(this.attachmentsListWidget, true);
  }

  public init(): void {
    if (
      location.pathname.includes('/lk/resident/reports') ||
      location.pathname.includes('/report-form')
    ) {
      const reportId = this.getReportIdFromUrl();

      // 1. Добавляем Id в форму
      this.injectReportIdIntoForm(reportId);

      // 2. Отключаем Enter как submit
      this.setupFormKeyNavigation();

      // 3. Инициализация виджетов
      this.attachmentsListWidget.init();
      this.formSubmitButton = document.querySelector('[id^="reportFormButton"]');
      if (this.formElement) {
        try {
          this.reportFormSignPresenter = initReportFormSignWidget(this.formElement);
        } catch (error) {
          console.error('ReportForm: не удалось инициализировать виджет подписи', error);
          this.reportFormSignPresenter = null;
        }
      }
      this.reportFormWidget.init();

      // 4. Настройка режима
      this.switchFormMode(reportId ? 'edit' : 'create');
    }
  }

  private injectReportIdIntoForm(reportId: number | null): void {
    if (!this.formElement || reportId === null) return;

    const existing = this.formElement.querySelector('input[name="Id"]');
    if (existing) existing.remove();

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'Id';
    hiddenInput.value = reportId.toString();
    this.formElement.appendChild(hiddenInput);
  }

  private setupFormKeyNavigation(): void {
    if (!this.formElement) return;

    this.formElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const target = e.target;

        if (
          !target ||
          !(
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            target instanceof HTMLSelectElement
          )
        ) {
          return;
        }

        if (target instanceof HTMLTextAreaElement) {
          return;
        }

        e.preventDefault();

        const focusable = Array.from(
          this.formElement!.querySelectorAll<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
          >('input, select, textarea'),
        ).filter((el) => !el.disabled && el.offsetParent !== null);

        const idx = focusable.indexOf(target);
        if (idx >= 0 && idx < focusable.length - 1) {
          focusable[idx + 1].focus();
        }
      }
    });
  }

  private switchFormMode(mode: 'create' | 'edit'): void {
    if (!this.formSubmitButton || !this.formElement) {
      console.error(
        'ReportForm: switchFormMode — не найдены кнопка отправки или форма (reportForm / reportFormButton).',
      );
      return;
    }

    const successMessageEl = document.createElement('div');
    const errorMessageEl = document.createElement('div');
    errorMessageEl.innerHTML =
      "<p class='toast-template__title'>Что-то пошло не так</p><p class='toast-template__description'>Пожалуйста, попробуйте еще раз</p>";

    if (mode === 'create') {
      successMessageEl.innerHTML =
        "<p class='toast-template__title'>Отчёт успешно сохранен</p><p class='toast-template__description'>и отправлен в управляющую компанию ИНТЦ</p>";

      this.formSubmitButton.type = 'submit';
      this.formSubmitButton.textContent = 'Подписать и отправить';

      this.formWidget.init({
        onFetch: () => {
          // setTimeout(() => {
          //   window.location.href = '/lk/resident/?tab=2';
          // }, 5000);
        },
        onSubmitting: (isSubmitting) => {
          this.updateSubmitButtonState(isSubmitting, 'Создание...');
        },
        onSuccess: async (result) => {
          await this.runPrepareSignAfterSave(result);
        },
        errorMessage: errorMessageEl,
        successMessage: successMessageEl,
        noResetAfterSubmit: true,
        disableDefaultButtonHandling: true,
      });

      this.formSubmitButton.removeAttribute('data-bs-toggle');
      this.formSubmitButton.removeAttribute('data-bs-target');
    }

    if (mode === 'edit') {
      successMessageEl.innerHTML =
        "<p class='toast-template__title'>Отчёт успешно изменен</p><p class='toast-template__description'>и отправлен в управляющую компанию ИНТЦ</p>";

      this.formSubmitButton.type = 'button';
      this.formSubmitButton.textContent = 'Сохранить отчёт';
      this.formSubmitButton.setAttribute('data-bs-toggle', 'modal');
      this.formSubmitButton.setAttribute('data-bs-target', this.EDIT_MODAL_ID);

      this.formWidget.init({
        onFetch: () => {
          // Ничего не делаем
        },
        onSubmitting: (isSubmitting) => {
          this.updateSubmitButtonState(isSubmitting, 'Сохранение...');
        },
        onSuccess: async (result) => {
          await this.runPrepareSignAfterSave(result);
        },
        errorMessage: errorMessageEl,
        successMessage: successMessageEl,
        noResetAfterSubmit: true,
        disableDefaultButtonHandling: true,
      });
    }
  }

  private async runPrepareSignAfterSave(result: unknown): Promise<void> {
    if (!this.reportFormSignPresenter) {
      console.error(
        'ReportForm: виджет подписи не инициализирован — handlePrepareSuccess не вызван.',
      );
      return;
    }
    try {
      await this.reportFormSignPresenter.handlePrepareSuccess(result);
    } catch (error) {
      console.error('ReportForm: ошибка при подготовке подписи после сохранения отчёта', error);
    }
  }

  private getReportIdFromUrl(): number | null {
    const params = new URLSearchParams(window.location.search);
    const reportId = params.get('Id');
    const num = reportId ? Number(reportId) : NaN;
    return isNaN(num) ? null : num;
  }

  private updateSubmitButtonState(isSubmitting: boolean, savingText: string): void {
    if (!this.formSubmitButton) return;

    const indicatorId = 'report-form-saving-indicator';
    let indicator = document.getElementById(indicatorId);

    if (isSubmitting) {
      if (!indicator) {
        indicator = document.createElement('span');
        indicator.id = indicatorId;
        indicator.className = 'me-2 text-muted';
        indicator.style.fontSize = '0.9em';
        indicator.innerHTML = savingText;
        // Вставляем ПЕРЕД основной кнопкой
        this.formSubmitButton.parentNode?.insertBefore(indicator, this.formSubmitButton);
      } else {
        indicator.innerHTML = savingText;
      }

      // Блокируем ОСНОВНУЮ кнопку (визуальная обратная связь)
      this.formSubmitButton.disabled = true;
    } else {
      if (indicator) {
        indicator.remove();
      }
      this.formSubmitButton.disabled = false;
    }
  }
}

registerPageInit('report-form', () => {
  const page = new ReportForm();
  page.init();
});
